import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, documentId } from 'firebase/firestore';
import { db } from '../../../../services/firebase';

export const useProfessorExpiringStudents = (idTenant, professorId, period) => {
    const [expiringStudents, setExpiringStudents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!idTenant || !professorId || !period) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                // 1. Obter turmas deste professor
                const classesQ = query(
                    collection(db, "tenants", idTenant, "classes"), 
                    where("idEmployee", "==", professorId)
                );
                const classesSnap = await getDocs(classesQ);
                const classIds = new Set(classesSnap.docs.map(d => d.id));

                if (classIds.size === 0) {
                    setExpiringStudents([]);
                    setLoading(false);
                    return;
                }

                // 2. Obter contratos vencendo neste mês (period = YYYY-MM)
                const [year, month] = period.split("-");
                const startDate = `${period}-01`;
                const lastDay = new Date(year, month, 0).getDate();
                const endDate = `${period}-${String(lastDay).padStart(2, '0')}`;

                const membershipsQ = query(
                    collection(db, "tenants", idTenant, "memberMemberships"),
                    where("endDate", ">=", startDate),
                    where("endDate", "<=", endDate)
                );
                const membershipsSnap = await getDocs(membershipsQ);
                
                const expiringMemberIds = [];
                const membershipsMap = {};
                membershipsSnap.forEach(doc => {
                    const data = doc.data();
                    if (data.idMember && data.status !== "canceled") {
                        expiringMemberIds.push(data.idMember);
                        membershipsMap[data.idMember] = data;
                    }
                });

                if (expiringMemberIds.length === 0) {
                    setExpiringStudents([]);
                    setLoading(false);
                    return;
                }

                // 3. Buscar detalhes dos membros (dividido em blocos de 10 para o 'in')
                const uniqueIds = [...new Set(expiringMemberIds)];
                const chunks = [];
                for (let i = 0; i < uniqueIds.length; i += 10) {
                    chunks.push(uniqueIds.slice(i, i + 10));
                }

                const myStudents = [];
                for (const chunk of chunks) {
                    // Buscar todos os contratos desses alunos para ver se já renovaram no futuro
                    const chunkMembershipsQ = query(
                        collection(db, "tenants", idTenant, "memberMemberships"),
                        where("idMember", "in", chunk)
                    );
                    const chunkMembershipsSnap = await getDocs(chunkMembershipsQ);
                    
                    const maxEndDateByMember = {};
                    chunkMembershipsSnap.forEach(doc => {
                        const mData = doc.data();
                        // Considera todos os contratos não cancelados
                        if (mData.status !== "canceled" && mData.endDate) {
                            const currentMax = maxEndDateByMember[mData.idMember] || "";
                            if (mData.endDate > currentMax) {
                                maxEndDateByMember[mData.idMember] = mData.endDate;
                            }
                        }
                    });

                    const membersQ = query(
                        collection(db, "tenants", idTenant, "members"),
                        where(documentId(), "in", chunk)
                    );
                    const membersSnap = await getDocs(membersQ);
                    membersSnap.forEach(doc => {
                        const data = doc.data();
                        const enrolledClassIds = data.enrolledClassIds || [];
                        const hasMyClass = enrolledClassIds.some(cid => classIds.has(cid));
                        
                        // O aluno renovou se a data máxima de fim dos contratos dele for maior que o fim deste mês
                        const maxEnd = maxEndDateByMember[doc.id] || membershipsMap[doc.id].endDate;
                        const hasRenewed = maxEnd > endDate;

                        if (hasMyClass && !hasRenewed) {
                            myStudents.push({
                                id: doc.id,
                                name: data.fullName || data.name || "Sem Nome",
                                photoUrl: data.photoUrl || null,
                                endDate: maxEnd,
                                risk: "Alto"
                            });
                        }
                    });
                }

                // Calcular risco de abandono (Heurística simples para não gastar reads extras de frequência)
                // Usamos a data de término. Se falta menos de 7 dias, é Crítico. Se falta menos de 15, Alto.
                const today = new Date();
                const processedStudents = myStudents.map(student => {
                    const end = new Date(student.endDate);
                    const diffDays = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
                    
                    let risk = "Baixo";
                    let riskColor = "#34c38f"; // Verde
                    
                    if (diffDays <= 5) {
                        risk = "Crítico";
                        riskColor = "#f46a6a"; // Vermelho
                    } else if (diffDays <= 15) {
                        risk = "Alto";
                        riskColor = "#f1b44c"; // Laranja
                    }

                    return { ...student, diffDays, risk, riskColor };
                });

                // Ordenar por data de expiração mais próxima
                processedStudents.sort((a, b) => new Date(a.endDate) - new Date(b.endDate));

                setExpiringStudents(processedStudents);
            } catch (err) {
                console.error("Error fetching expiring students:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [idTenant, professorId, period]);

    return { expiringStudents, loading };
};
